import { useMemo } from 'react';
import { buildMetricValueItems } from '@/features/metrics/components/metricValueItems';
import {
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
} from '@/features/metrics/components/statusMetricCards';
import { buildThresholdGuideLines } from '@/features/metrics/lib/chartThresholdGuides';
import { formatStatusLabel } from '@/ui/primitives';
import type { EntityStatusKey } from './useEntityTrendsPage.types';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type {
  MetricCardItem,
  MetricCardOption,
} from '@/features/metrics/components/statusMetricCards';
import type { MetricThresholdGroup } from '@flowqa/shared';

const totalMetricCardLabels = {
  suite: 'Suite Runs',
  test: 'Test Runs',
} as const;

type UseEntityTrendsMetricDisplayParams<TSummary extends { total: number }> = {
  statusOptions: Array<{ value: EntityStatusKey; label: string }>;
  metricsScope: 'suite' | 'test';
  counts: Partial<Record<EntityStatusKey, number>>;
  deltas: Partial<Record<EntityStatusKey, number>> | undefined;
  summary: TSummary | null;
  previousCounts: Partial<Record<EntityStatusKey, number>>;
  previousSummary: TSummary | null;
  summaryIsFetching: boolean;
  showDelta: boolean;
  loadExecutionMetrics: boolean;
  executionMetrics: Parameters<typeof buildMetricValueItems>[0]['metrics'];
  previousMetrics: Parameters<typeof buildMetricValueItems>[0]['previousMetrics'];
  entityThresholds: MetricThresholdGroup;
  totalSecondaryValue?: string;
};

type UseEntityTrendsMetricDisplayResult = {
  metricCards: MetricCardItem[];
  metricValueItems: MetricValueItem[];
  passRateGuideLines: Array<{ value: number; color: string }>;
  frequencyGuideLines: Array<{ value: number; color: string }>;
  durationGuideLines: Array<{ value: number; color: string }>;
};

export const useEntityTrendsMetricDisplay = <TSummary extends { total: number }>({
  statusOptions,
  metricsScope,
  counts,
  deltas,
  summary,
  previousCounts,
  previousSummary,
  summaryIsFetching,
  showDelta,
  loadExecutionMetrics,
  executionMetrics,
  previousMetrics,
  entityThresholds,
  totalSecondaryValue,
}: UseEntityTrendsMetricDisplayParams<TSummary>): UseEntityTrendsMetricDisplayResult => {
  const metricOptions = useMemo<Array<MetricCardOption<EntityStatusKey>>>(() => {
    const baseOptions = mapStatusOptionsToMetricOptions(statusOptions, resolveMetricTone) as Array<
      MetricCardOption<EntityStatusKey>
    >;
    const hasUntested = baseOptions.some((option) => option.value === 'untested');
    if (hasUntested) {
      return baseOptions;
    }
    return [
      ...baseOptions,
      {
        value: 'untested',
        label: formatStatusLabel('untested'),
        tone: resolveMetricTone('untested'),
      },
    ];
  }, [statusOptions]);

  const metricCards = useMemo(
    () =>
      buildStatusMetricCards({
        options: metricOptions,
        counts,
        deltas,
        total: summary?.total,
        totalSecondaryValue,
        previousCounts,
        previousTotal: previousSummary?.total,
        loading: summaryIsFetching,
        showDelta,
        totalLabel: totalMetricCardLabels[metricsScope],
        hideZeroCountStatuses: ['errored', 'untested'],
      }),
    [
      counts,
      deltas,
      metricOptions,
      metricsScope,
      previousCounts,
      previousSummary?.total,
      showDelta,
      summary?.total,
      summaryIsFetching,
      totalSecondaryValue,
    ],
  );

  const summaryPassRate = useMemo(() => {
    const total = summary?.total ?? null;
    if (total === null || total <= 0) return null;
    return ((counts.passed ?? 0) / total) * 100;
  }, [counts.passed, summary?.total]);

  const previousSummaryPassRate = useMemo(() => {
    if (!showDelta || !previousSummary) return null;
    const total = previousSummary.total;
    if (total <= 0) return null;
    return ((previousCounts.passed ?? 0) / total) * 100;
  }, [previousCounts.passed, previousSummary, showDelta]);

  const metricValueItems = useMemo(() => {
    if (!loadExecutionMetrics) {
      return [];
    }
    return buildMetricValueItems({
      metrics: executionMetrics,
      previousMetrics: showDelta ? previousMetrics : null,
      passRateOverride: summaryPassRate,
      previousPassRateOverride: previousSummaryPassRate,
      thresholds: entityThresholds,
      showDelta,
    });
  }, [
    entityThresholds,
    executionMetrics,
    loadExecutionMetrics,
    previousMetrics,
    previousSummaryPassRate,
    showDelta,
    summaryPassRate,
  ]);

  const passRateGuideLines = useMemo(
    () => buildThresholdGuideLines(entityThresholds.passRate),
    [entityThresholds.passRate],
  );
  const frequencyGuideLines = useMemo(
    () => buildThresholdGuideLines(entityThresholds.frequency),
    [entityThresholds.frequency],
  );
  const durationGuideLines = useMemo(
    () => buildThresholdGuideLines(entityThresholds.avgDuration),
    [entityThresholds.avgDuration],
  );

  return {
    metricCards,
    metricValueItems,
    passRateGuideLines,
    frequencyGuideLines,
    durationGuideLines,
  };
};
